import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { StatusBadge, ConfidenceBadge } from '../components/Badges';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiCpu, FiLink, FiAlertTriangle, FiEdit2 } = FiIcons;

const PartDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { parts, crossRefs, userRole } = useAppContext();

  const part = parts.find(p => p.id === id);

  if (!part) return <div className="panel text-center py-20 text-slate-900 dark:text-white">Part not found. Return to search and try again.</div>;

  const equivalents = crossRefs
    .filter(cr => cr.part_a_id === id || cr.part_b_id === id)
    .map(cr => {
      const eqId = cr.part_a_id === id ? cr.part_b_id : cr.part_a_id;
      return { ...parts.find(p => p.id === eqId), confidence: cr.confidence };
    })
    .filter(p => p.id);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
          <SafeIcon icon={FiArrowLeft} /> Back
        </button>
        {userRole === 'Admin' && (
          <Link to="/admin" className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white border border-slate-200 dark:border-slate-700 rounded-lg flex items-center gap-2 shadow-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
            <SafeIcon icon={FiEdit2} className="w-4 h-4" /> Admin Mgmt
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 relative overflow-hidden shadow-sm">
            <div className="relative z-10">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-500 uppercase tracking-widest">{part.appl_brand}</span>
                  <h1 className="text-3xl font-semibold text-slate-900 dark:text-white font-mono mt-1 break-all">{part.look_up_pn}</h1>
                  <p className="text-lg text-slate-600 dark:text-slate-400 mt-2">{part.descrip1}</p>
                </div>
                <StatusBadge status={part.status} />
              </div>
              
              {part.descrip2 && <p className="text-sm text-slate-500 italic mt-2">{part.descrip2}</p>}

              {part.status === 'Discontinued' && (
                <div className="mt-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl flex gap-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-red-500 dark:text-red-400" />
                  <p className="text-sm text-red-700 dark:text-red-100 font-bold">Discontinued OEM Part. See cross-references for alternatives.</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2 mb-6">
              <SafeIcon icon={FiCpu} className="text-blue-600 dark:text-blue-500" /> Technical Data
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { label: 'Equipment Type', value: part.equip_type },
                { label: 'Manufacturer', value: part.appl_mfr },
                { label: 'Design', value: part.equip_dsgn },
                { label: 'Part Category', value: part.part_type },
                { label: 'Location', value: part.location },
                { label: 'Original PN', value: part.part_numbr }
              ].map(spec => (
                <div key={spec.label} className="border-b border-slate-100 dark:border-slate-800 pb-2">
                  <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">{spec.label}</div>
                  <div className="text-slate-800 dark:text-slate-200 font-medium">{spec.value || 'N/A'}</div>
                </div>
              ))}
            </div>

            {part.comments && (
              <div className="mt-8 p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
                <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">Technician Comments</div>
                <p className="text-slate-700 dark:text-slate-300 text-sm whitespace-pre-wrap">{part.comments}</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm dark:shadow-2xl">
            <h2 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2 mb-4">
              <SafeIcon icon={FiLink} className="text-emerald-500" /> Alternatives
            </h2>
            <div className="space-y-3">
              {equivalents.map(eq => (
                <Link key={eq.id} to={`/part/${eq.id}`} className="block p-4 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl hover:border-blue-500 dark:hover:border-blue-500 transition-all">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] font-bold text-slate-500">{eq.appl_brand}</span>
                    <ConfidenceBadge confidence={eq.confidence} />
                  </div>
                  <div className="font-mono text-slate-900 dark:text-white font-bold">{eq.look_up_pn}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{eq.descrip1}</div>
                </Link>
              ))}
              {equivalents.length === 0 && (
                <div className="text-center py-8 text-slate-500 dark:text-slate-600 text-sm border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                  No cross-references found.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartDetail;